import React, { useState } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  TextField,
  Tabs,
  Tab,
  Alert,
  Divider,
  InputAdornment,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Chip
} from '@mui/material';
import {
  FamilyRestroom,
  Add,
  QrCode2,
  VpnKey,
  ChildCare,
  CheckCircle
} from '@mui/icons-material';
import { useFamilyId } from '../contexts/FamilyIdContext';
import { useNavigate } from 'react-router-dom';
import { registerChild, validatePIN } from '../utils/childAuthHelper';

function TabPanel({ children, value, index }) {
  return (
    <div hidden={value !== index}>
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

function FamilySetup() {
  const [tabValue, setTabValue] = useState(0);
  const [familyName, setFamilyName] = useState('');
  const [inviteCode, setInviteCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Child addition state (used after family is created)
  const [createdFamilyId, setCreatedFamilyId] = useState(null);
  const [showAddChildren, setShowAddChildren] = useState(false);
  const [addedChildren, setAddedChildren] = useState([]);
  const [childForm, setChildForm] = useState({
    firstName: '',
    pin: '',
    confirmPin: '',
    dateOfBirth: ''
  });
  const [childError, setChildError] = useState('');
  const [childLoading, setChildLoading] = useState(false);
  
  const { createFamily, joinFamily } = useFamilyId();
  const navigate = useNavigate();

  const handleCreateFamily = async () => {
    if (!familyName.trim()) {
      setError('Please enter a family name');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // createFamily returns the familyId string
      const newFamilyId = await createFamily(familyName);
      setCreatedFamilyId(newFamilyId || null);
      setShowAddChildren(true);
      setLoading(false);
    } catch (error) {
      setError(error.message);
      setLoading(false);
    }
  };

  const handleAddChild = async () => {
    setChildError('');
    if (!childForm.firstName || childForm.firstName.trim().length < 2) {
      setChildError("Please enter the child's first name (at least 2 characters)");
      return;
    }
    if (!validatePIN(childForm.pin)) {
      setChildError('PIN must be exactly 4 digits');
      return;
    }
    if (childForm.pin !== childForm.confirmPin) {
      setChildError('PINs do not match');
      return;
    }
    if (!createdFamilyId) {
      setChildError('Family ID not found. Please go to Parent Dashboard and add children from there.');
      return;
    }

    setChildLoading(true);
    try {
      const childData = await registerChild(
        createdFamilyId,
        childForm.firstName,
        childForm.pin,
        childForm.dateOfBirth || null
      );
      setAddedChildren(prev => [...prev, { ...childData, firstName: childForm.firstName }]);
      setChildForm({ firstName: '', pin: '', confirmPin: '', dateOfBirth: '' });
    } catch (err) {
      setChildError(err.message);
    } finally {
      setChildLoading(false);
    }
  };

  const handleJoinFamily = async () => {
    if (!inviteCode.trim()) {
      setError('Please enter an invite code');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await joinFamily(inviteCode.toUpperCase());
      // Navigate based on user role (will be determined by userProfile)
      navigate('/child');
    } catch (error) {
      setError(error.message);
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #8E24AA 0%, #00BCD4 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 2
      }}
    >
      <Container maxWidth="sm">
        <Paper
          elevation={10}
          sx={{
            borderRadius: 3,
            overflow: 'hidden',
            background: 'rgba(255, 255, 255, 0.95)'
          }}
        >
          {/* Header */}
          <Box
            sx={{
              background: 'linear-gradient(45deg, #8E24AA, #00BCD4)',
              color: 'white',
              textAlign: 'center',
              py: 4,
              px: 3
            }}
          >
            <Box sx={{ fontSize: 60, mb: 2 }}>🤖👨‍👩‍👧‍👦</Box>
            <Typography 
              variant="h4" 
              component="h1" 
              fontWeight="bold"
              sx={{ fontFamily: "'Poppins', sans-serif" }}
            >
              Welcome to Earn-E!
            </Typography>
            <Typography variant="body1" sx={{ mt: 1, opacity: 0.9 }}>
              Let's set up your family
            </Typography>
          </Box>

          {/* Tabs */}
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs
              value={tabValue}
              onChange={(e, newValue) => {
                setTabValue(newValue);
                setError('');
              }}
              centered
            >
              <Tab
                icon={<Add />}
                label="Create Family"
                iconPosition="start"
              />
              <Tab
                icon={<VpnKey />}
                label="Join Family"
                iconPosition="start"
              />
            </Tabs>
          </Box>

          {error && (
            <Box sx={{ p: 2 }}>
              <Alert severity="error">{error}</Alert>
            </Box>
          )}

          {/* Create Family Tab */}
          <TabPanel value={tabValue} index={0}>
            <Box sx={{ px: 3, pb: 3 }}>
              {!showAddChildren ? (
                <>
                  <Typography variant="h6" gutterBottom sx={{ fontFamily: "'Poppins', sans-serif" }}>
                    Create Your Family Account
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    You'll be the family administrator and can invite other members later.
                  </Typography>

                  <TextField
                    label="Family Name"
                    placeholder="The Smith Family"
                    value={familyName}
                    onChange={(e) => setFamilyName(e.target.value)}
                    fullWidth
                    sx={{ mt: 2 }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <FamilyRestroom color="primary" />
                        </InputAdornment>
                      ),
                    }}
                  />

                  <Button
                    variant="contained"
                    size="large"
                    fullWidth
                    onClick={handleCreateFamily}
                    disabled={loading}
                    sx={{
                      mt: 3,
                      py: 1.5,
                      fontSize: '1.1rem',
                      fontWeight: 600,
                      background: 'linear-gradient(45deg, #8E24AA, #00BCD4)',
                      '&:hover': {
                        background: 'linear-gradient(45deg, #7B1FA2, #0097A7)',
                      }
                    }}
                  >
                    {loading ? 'Creating Family...' : 'Create Family 🚀'}
                  </Button>

                  <Divider sx={{ my: 3 }}>
                    <Typography variant="caption" color="text.secondary">
                      What happens next?
                    </Typography>
                  </Divider>

                  <Box sx={{ bgcolor: 'info.light', p: 2, borderRadius: 2 }}>
                    <Typography variant="body2">
                      ✅ You'll get a unique invite code<br />
                      ✅ Share it with family members<br />
                      ✅ They can join your family instantly!<br />
                      ✅ All your data stays private
                    </Typography>
                  </Box>

                  <Box sx={{ bgcolor: 'warning.light', p: 2, borderRadius: 2, mt: 2 }}>
                    <Typography variant="body2">
                      📱 <strong>Screen time rules require Android devices.</strong> When enabled, Earn-E locks the device during chores and scheduled events — keeping kids focused. iOS support coming soon.
                    </Typography>
                  </Box>
                </>
              ) : (
                /* Add Children Panel — shown after family is created */
                <>
                  <Box sx={{ textAlign: 'center', mb: 2 }}>
                    <CheckCircle sx={{ fontSize: 48, color: 'success.main' }} />
                    <Typography variant="h6" gutterBottom sx={{ fontFamily: "'Poppins', sans-serif" }}>
                      Family Created! 🎉
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Optionally add your children now (you can also do this from your dashboard).
                    </Typography>
                  </Box>

                  {childError && (
                    <Alert severity="error" sx={{ mb: 2 }}>{childError}</Alert>
                  )}

                  {addedChildren.length > 0 && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        Added children:
                      </Typography>
                      <List dense>
                        {addedChildren.map((c, i) => (
                          <ListItem key={i}>
                            <ListItemAvatar>
                              <Avatar src={c.avatar} sx={{ bgcolor: 'warning.main' }}>
                                <ChildCare />
                              </Avatar>
                            </ListItemAvatar>
                            <ListItemText
                              primary={c.firstName}
                              secondary={c.dateOfBirth ? `DOB: ${c.dateOfBirth}` : 'No DOB provided'}
                            />
                            <Chip label="Added ✓" color="success" size="small" />
                          </ListItem>
                        ))}
                      </List>
                      <Divider sx={{ my: 2 }} />
                    </Box>
                  )}

                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <TextField
                      label="Child's First Name"
                      placeholder="Emma"
                      value={childForm.firstName}
                      onChange={(e) => setChildForm({ ...childForm, firstName: e.target.value })}
                      fullWidth
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <ChildCare color="secondary" />
                          </InputAdornment>
                        ),
                      }}
                    />

                    <TextField
                      label="Date of Birth"
                      type="date"
                      value={childForm.dateOfBirth}
                      onChange={(e) => setChildForm({ ...childForm, dateOfBirth: e.target.value })}
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ max: new Date().toISOString().split('T')[0] }}
                      fullWidth
                      helperText="Used to provide age-appropriate features (COPPA compliance)"
                    />

                    <TextField
                      label="4-Digit PIN"
                      type="password"
                      value={childForm.pin}
                      onChange={(e) => setChildForm({ ...childForm, pin: e.target.value })}
                      fullWidth
                      placeholder="1234"
                      inputProps={{ maxLength: 4, pattern: '[0-9]*', inputMode: 'numeric' }}
                      helperText="Child uses this to log in"
                    />

                    <TextField
                      label="Confirm PIN"
                      type="password"
                      value={childForm.confirmPin}
                      onChange={(e) => setChildForm({ ...childForm, confirmPin: e.target.value })}
                      fullWidth
                      placeholder="1234"
                      inputProps={{ maxLength: 4, pattern: '[0-9]*', inputMode: 'numeric' }}
                    />

                    <Button
                      variant="outlined"
                      color="secondary"
                      onClick={handleAddChild}
                      disabled={childLoading}
                      startIcon={<Add />}
                      sx={{ py: 1.2 }}
                    >
                      {childLoading ? 'Adding...' : 'Add This Child'}
                    </Button>
                  </Box>

                  <Divider sx={{ my: 3 }} />

                  <Button
                    variant="contained"
                    size="large"
                    fullWidth
                    onClick={() => navigate('/parent')}
                    sx={{
                      py: 1.5,
                      fontSize: '1.1rem',
                      fontWeight: 600,
                      background: 'linear-gradient(45deg, #8E24AA, #00BCD4)',
                      '&:hover': {
                        background: 'linear-gradient(45deg, #7B1FA2, #0097A7)',
                      }
                    }}
                  >
                    Go to Dashboard →
                  </Button>
                </>
              )}
            </Box>
          </TabPanel>

          {/* Join Family Tab */}
          <TabPanel value={tabValue} index={1}>
            <Box sx={{ px: 3, pb: 3 }}>
              <Typography variant="h6" gutterBottom sx={{ fontFamily: "'Poppins', sans-serif" }}>
                Join an Existing Family
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                Enter the invite code your parent or family admin shared with you.
              </Typography>

              <Box sx={{ textAlign: 'center', my: 3 }}>
                <QrCode2 sx={{ fontSize: 80, color: 'primary.main', opacity: 0.5 }} />
              </Box>

              <TextField
                label="Invite Code"
                placeholder="ABC123"
                value={inviteCode}
                onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
                fullWidth
                sx={{ mt: 2 }}
                inputProps={{
                  maxLength: 8,
                  style: { 
                    textTransform: 'uppercase', 
                    letterSpacing: '0.2em', 
                    textAlign: 'center', 
                    fontSize: '1.5rem',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <VpnKey color="primary" />
                    </InputAdornment>
                  ),
                }}
              />

              <Button
                variant="contained"
                size="large"
                fullWidth
                onClick={handleJoinFamily}
                disabled={loading || inviteCode.length !== 8}
                sx={{
                  mt: 3,
                  py: 1.5,
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  background: 'linear-gradient(45deg, #8E24AA, #00BCD4)',
                  '&:hover': {
                    background: 'linear-gradient(45deg, #7B1FA2, #0097A7)',
                  }
                }}
              >
                {loading ? 'Joining Family...' : 'Join Family 🎉'}
              </Button>

              <Divider sx={{ my: 3 }}>
                <Typography variant="caption" color="text.secondary">
                  Need help?
                </Typography>
              </Divider>

              <Box sx={{ bgcolor: 'warning.light', p: 2, borderRadius: 2 }}>
                <Typography variant="body2">
                  💡 <strong>Tip:</strong> Ask your parent for the 8-character invite code.<br />
                  It looks like: <code style={{ fontSize: '1.1em', fontWeight: 'bold' }}>ABC123XY</code>
                </Typography>
              </Box>
            </Box>
          </TabPanel>

          {/* Footer */}
          <Box sx={{ p: 2, textAlign: 'center', bgcolor: 'grey.50' }}>
            <Typography variant="caption" color="text.secondary">
              🤖 Powered by Earn-E AI Assistant
            </Typography>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
}

export default FamilySetup;












